import { useState, useEffect } from "react";
import styled from "styled-components";
import { Col, Row, Button, Label, Badge } from "reactstrap";
import APLStudentEventCard from "./APLStudentEventCard";
import { useQueryClient } from "react-query";
import { DateRangePicker } from "react-nice-dates";
import { sv } from "date-fns/locale";
import "react-nice-dates/build/style.css";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FormattedMessage } from "react-intl";
import { injectIntl } from "react-intl";

const APLMoments = ({ studentEvents, level, periodId, objectId, intl }) => {
  const queryClient = useQueryClient();
  const [sortedEvents, setSortedEvents] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [eventStatuses, setEventStatuses] = useState([]);
  const [workSteps, setWorkSteps] = useState([]);

  const statusOptions = [
    {
      value: "SignedBySupervisor",
      label: <FormattedMessage id="SignedBySupervisor" />,
    },
    {
      value: "UnsignedBySupervisor",
      label: <FormattedMessage id="UnsignedBySupervisor" />,
    },
    {
      value: "SignedByTeacher",
      label: <FormattedMessage id="SignedByTeacher" />,
    },
    {
      value: "UnsignedByTeacher",
      label: <FormattedMessage id="UnsignedByTeacher" />,
    },
    { value: "Commented", label: <FormattedMessage id="Commented" /> },
  ];

  const filterWorkSteps = [
    ...new Map(
      studentEvents.data
        .map((event) => ({
          label: event.workStepComponent.workStepName,
          value: event.workStepComponent.workStepName,
        }))
        .map((item) => [item.value, item])
    ).values(),
  ];

  const handleResetAll = () => {
    setStartDate(null);
    setEndDate(null);
    setEventStatuses([]);
    setWorkSteps([]);
  };

  useEffect(() => {
    queryClient.invalidateQueries("FetchEvents");
  }, [queryClient]);

  useEffect(() => {
    let filteredEvents = [...studentEvents.data];

    if (startDate && endDate) {
      const toDateOnly = (d) =>
        new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const start = toDateOnly(startDate);
      const end = toDateOnly(endDate);

      filteredEvents = filteredEvents.filter((item) => {
        var createdDate = toDateOnly(new Date(item.created));
        return createdDate >= start && createdDate <= end;
      });
    }

    if (eventStatuses && eventStatuses.length > 0) {
      filteredEvents = filteredEvents.filter((item) => {
        var statusFilters = {
          SignedBySupervisor: item.signedBySupervisor,
          UnsignedBySupervisor: !item.signedBySupervisor,
          SignedByTeacher: item.signedByTeacher,
          UnsignedByTeacher: !item.signedByTeacher,
          Commented: item.comments.length > 0,
        };
        return eventStatuses.some((status) => statusFilters[status.value]);
      });
    }

    if (workSteps && workSteps.length > 0) {
      filteredEvents = filteredEvents.filter((item) =>
        workSteps.some(
          (step) => step.value === item.workStepComponent.workStepName
        )
      );
    }

    filteredEvents.sort(function (a, b) {
      var c = new Date(a.created);
      var d = new Date(b.created);
      return d - c;
    });

    setSortedEvents(filteredEvents);
  }, [eventStatuses, workSteps, startDate, endDate, studentEvents]);

  return (
    <div className="mx-auto" style={{ width: "100%" }}>
      {sortedEvents && (
        <>
          <div
            className="mb-3"
            style={{
              zIndex: "99",
              borderRadius: "10px",
              boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.16)",
            }}
          >
            <div className="d-flex justify-content-between align-items-center m-2">
              <p className="mb-0 text-primary" style={{ fontWeight: 600 }}>
                <FormattedMessage id="FilterEventsBy" />
              </p>
              <div className="ms-auto me-2 mt-2">
                <Button outline onClick={handleResetAll} size="sm">
                  <FontAwesomeIcon icon={faTimes} />{" "}
                  <FormattedMessage id="ResetAll" />
                </Button>
              </div>
            </div>

            <Row className="ms-1 me-1">
              <Col md={4}>
                <div className="mb-3">
                  <Label className="mb-1">
                    <FormattedMessage id="Date" />
                  </Label>
                  <div className="d-flex justify-content-end">
                    <div className="w-100">
                      <DateRangePicker
                        startDate={startDate}
                        endDate={endDate}
                        onStartDateChange={setStartDate}
                        onEndDateChange={setEndDate}
                        format="yyyy MMM dd"
                        locale={sv}
                      >
                        {({ startDateInputProps, focus }) => (
                          <input
                            className={`form-control ${
                              focus ? "border-primary rounded" : "rounded"
                            }`}
                            {...startDateInputProps}
                            value={
                              startDate && endDate
                                ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                                : ""
                            }
                            placeholder={intl.formatMessage({
                              id: "SelectDateRange",
                            })}
                          />
                        )}
                      </DateRangePicker>
                    </div>
                    <Button
                      outline
                      size="sm"
                      onClick={() => {
                        setStartDate(null);
                        setEndDate(null);
                      }}
                    >
                      ×
                    </Button>
                  </div>
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <Label className="mb-1">
                    <FormattedMessage id="Status" />
                  </Label>
                  <Select
                    options={statusOptions}
                    isClearable
                    isMulti
                    placeholder={<FormattedMessage id="SelectStatus" />}
                    value={eventStatuses}
                    onChange={setEventStatuses}
                  />
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <Label className="mb-1">
                    <FormattedMessage id="Workstep" />
                  </Label>
                  <Select
                    options={filterWorkSteps}
                    isClearable
                    isMulti
                    placeholder={<FormattedMessage id="SelectWorkstep" />}
                    value={workSteps}
                    onChange={setWorkSteps}
                  />
                </div>
              </Col>
            </Row>

            {(eventStatuses.length > 0 ||
              workSteps.length > 0 ||
              startDate ||
              endDate) && (
              <div className="ms-3 pb-2">
                <small className="text-muted me-2">
                  <FormattedMessage id="ActiveFilters" />
                </small>
                {startDate && endDate && (
                  <Badge color="light" className="me-1">
                    {`${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`}
                  </Badge>
                )}
                {eventStatuses.map((status) => (
                  <Badge key={status.value} color="light" className="me-1">
                    {status.label}
                  </Badge>
                ))}
                {workSteps.map((step) => (
                  <Badge key={step.value} color="light" className="me-1">
                    {step.label}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="mb-3">
            <h6>
              {sortedEvents.length} <FormattedMessage id="Of" />{" "}
              {studentEvents.data.length}{" "}
              <FormattedMessage id="WorkstepsFound" />
            </h6>
          </div>

          <ColWithoutScroll className="pb-5" style={{ overflow: "auto" }}>
            {sortedEvents.map((item, index) => {
              var date = new Date(item.created);
              return (
                <APLStudentEventCard
                  key={item.id}
                  item={item}
                  date={date}
                  index={index}
                  level={level}
                  periodId={periodId}
                  objectId={objectId}
                />
              );
            })}
          </ColWithoutScroll>
        </>
      )}
    </div>
  );
};

export default injectIntl(APLMoments);

const ColWithoutScroll = styled(Col)`
  &::-webkit-scrollbar {
    display: none;
  }
`;
